import {
  CreateDateColumn,
  UpdateDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  OneToMany,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, ID, Int } from 'type-graphql'
import { Domain } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { WorkflowExecution } from './workflow-execution.js'
import { WorkflowStatus, TriggerType } from '../types/workflow-types.js'

/**
 * LabelingWorkflow Entity
 *
 * Represents a labeling workflow definition with multiple steps.
 * Stores workflow configuration, steps, and trigger settings.
 */
@ObjectType({
  description: 'Entity representing a labeling workflow with orchestration steps'
})
@Entity('labeling_workflows')
@Index('ix_labeling_workflow_0', ['domain', 'projectId', 'status'], {
  where: '"deleted_at" IS NULL'
})
@Index('ix_labeling_workflow_1', ['domain', 'name'], {
  where: '"deleted_at" IS NULL'
})
export class LabelingWorkflow {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique workflow identifier' })
  id: string

  @ManyToOne(type => Domain)
  @Field(type => Domain, { nullable: true, description: 'Domain to which this workflow belongs' })
  domain?: Domain

  @RelationId((workflow: LabelingWorkflow) => workflow.domain)
  domainId?: string

  @ManyToOne(type => User)
  @Field(type => User, { nullable: true, description: 'User who created the workflow' })
  creator?: User

  @RelationId((workflow: LabelingWorkflow) => workflow.creator)
  creatorId?: string

  @Column()
  @Field({ description: 'Workflow name' })
  name: string

  @Column({ type: 'text', nullable: true })
  @Field({ nullable: true, description: 'Workflow description' })
  description?: string

  @Column({ type: 'int' })
  @Field(type => Int, { description: 'Label Studio project ID' })
  projectId: number

  @Column({ type: 'varchar', length: 50 })
  @Field(type => String, { description: 'Trigger type (Manual, Schedule, etc.)' })
  triggerType: TriggerType

  @Column({ type: 'text', nullable: true })
  @Field({ nullable: true, description: 'Trigger configuration as JSON' })
  triggerConfig?: string

  @Column({ type: 'jsonb' })
  steps: string // JSON string of WorkflowStep[] - internal storage

  @Column({ type: 'varchar', length: 50, default: WorkflowStatus.Draft })
  @Field(type => String, { description: 'Current workflow status' })
  status: WorkflowStatus

  @Column({ type: 'timestamptz', nullable: true })
  @Field({ nullable: true, description: 'Last execution time' })
  lastExecutedAt?: Date

  @Column({ type: 'timestamptz', nullable: true })
  @Field({ nullable: true, description: 'Next scheduled execution time' })
  nextExecutionAt?: Date

  @OneToMany(type => WorkflowExecution, execution => execution.workflow)
  @Field(type => [WorkflowExecution], { nullable: true, description: 'Executions of this workflow' })
  executions?: WorkflowExecution[]

  @CreateDateColumn({ type: 'timestamptz' })
  @Field({ description: 'Creation timestamp' })
  createdAt: Date

  @UpdateDateColumn({ type: 'timestamptz' })
  @Field({ description: 'Last update timestamp' })
  updatedAt: Date

  @Column({ type: 'timestamptz', nullable: true })
  @Field({ nullable: true, description: 'Soft delete timestamp' })
  deletedAt?: Date
}
